import { useEffect, useRef } from "react";
import { Button, ProgressBar } from "react-bootstrap";
import "./ChatBot.css";
import { ChatMessage as ChatMessageType } from "./ChatBot.types";
import { ChatMessage } from "./ChatMessage";
import type { ChatUiStrings } from "./chatUiLocale";

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

interface ChatMessageListProps {
  readonly messages: ChatMessageType[];
  readonly isTyping: boolean;
  readonly progressMessage?: string;
  readonly progressPercent?: number | null;
  readonly exportId?: string;
  readonly cancelExportJob?: (exportId: string) => void;
  readonly ui: ChatUiStrings;
  readonly elapsedSeconds?: number;
}

export function ChatMessageList({
  messages,
  isTyping,
  progressMessage,
  progressPercent,
  exportId,
  cancelExportJob,
  ui,
  elapsedSeconds,
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, progressMessage, progressPercent, exportId]);

  return (
    <div className="chat-message-list">
      {messages.length === 0 && !isTyping && !progressMessage && (
        <div className="empty-state">
          <h5>{ui.emptyTitle}</h5>
          <p className="text-muted">
            {ui.emptyLine1}
            <br />
            {ui.emptyLine2}
            <br />
            <b>{ui.emptyBilingualNote}</b>
          </p>
        </div>
      )}
      {messages.map((message) => (
        <ChatMessage key={message.id} message={message} />
      ))}
      {(progressMessage || progressPercent != null) && (
        <div className="chat-message-wrapper assistant">
          <div className="chat-message assistant-message">
            <div className="message-content">{progressMessage || ui.working}</div>
            {progressPercent != null && (
              <ProgressBar
                now={progressPercent}
                label={`${Math.round(progressPercent)}%`}
                className="mt-2"
                style={{ minHeight: "1.25rem" }}
              />
            )}
            {exportId && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-2"
                onClick={() => cancelExportJob?.(exportId)}
              >
                {ui.stop}
              </Button>
            )}
            {(elapsedSeconds ?? 0) > 0 && (
              <div className="text-muted mt-2" style={{ fontSize: "0.75em" }}>
                ⌛ {formatElapsed(elapsedSeconds ?? 0)}
              </div>
            )}
          </div>
        </div>
      )}
      {isTyping && (
        <div className="chat-message-wrapper assistant">
          <div className="chat-message assistant-message">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
            {(elapsedSeconds ?? 0) > 0 && (
              <div className="text-muted mt-2" style={{ fontSize: "0.75em" }}>
                ⌛ {formatElapsed(elapsedSeconds ?? 0)}
              </div>
            )}
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}
